import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  MenuItem,
  Button,
  IconButton,
  CircularProgress,
  Tooltip,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Alert,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  getAllBillingInvoices,
  downloadBillingInvoicePdf,
  sendBillingInvoiceReminder,
  refreshBillingPayments,
} from "app/services/api/billing";
import { getBEUrl } from "app/services/api/management";
import BillingBreadcrumbs from "./BillingBreadcrumbs";

/** Faktúra: issueDate/dueDate sú ISO stringy → DD.MM.RRRR (SK). */
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}.${d.getFullYear()}`;
};

const fmtMoney = (n) =>
  `${(Number(n) || 0).toLocaleString("sk-SK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;

const STATUS_COLOR = {
  created: "default",
  sent: "info",
  paid: "success",
  overdue: "error",
  cancelled: "default",
  error: "error",
};

const STATUS_OPTIONS = [
  "created",
  "sent",
  "paid",
  "overdue",
  "cancelled",
  "error",
];

// TESTOVACIA FÁZA: upomienky sa reálnym zákazníkom neposielajú — test posiela na tieto kontakty.
const REMINDER_TEST_EMAIL = "martin.valasek@datavision.sk";
const REMINDER_TEST_PHONE = "+421907186557";

const BillingInvoicesPage = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState({
    count: 0,
    withoutVat: 0,
    vat: 0,
    withVat: 0,
  });
  const [loading, setLoading] = useState(true);
  const [pdfLoadingId, setPdfLoadingId] = useState(null);

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [status, setStatus] = useState("");
  // Globálny import stavu úhrady z Abry (všetci zákazníci).
  const [syncingPayments, setSyncingPayments] = useState(false);

  // Server-side stránkovanie (20/stranu preddefinované). total = počet všetkých faktúr po filtri.
  const [page, setPage] = useState(0); // MUI = 0-based
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  // Bump → znovunačítanie bez zmeny čísla stránky (filtre / Obnoviť / po importe).
  const [reloadToken, setReloadToken] = useState(0);

  // Upomienka: potvrdzovací dialóg + výber kanálov (email/SMS).
  const [reminderInvoice, setReminderInvoice] = useState(null);
  const [reminderChannels, setReminderChannels] = useState({ email: true, sms: true });
  const [reminderSending, setReminderSending] = useState(false);
  // true = testovacie odoslanie (na naše kontakty, nie zákazníkovi).
  const [reminderTest, setReminderTest] = useState(false);
  // LEN pre test: cieľový backend (prostredie), na ktorý ide test request.
  const [reminderBackend, setReminderBackend] = useState("");

  // Pevný zoznam cieľových backendov pre test.
  const backendOptions = useMemo(
    () => [
      { url: "https://be.gastrokiosk.online", label: "Produkcia (be.gastrokiosk.online)" },
      { url: "https://staging.gastrokiosk.online", label: "Staging (staging.gastrokiosk.online)" },
      { url: "http://localhost:8080", label: "Lokálne (localhost:8080)" },
    ],
    [],
  );

  const label = (key, def) => t(`pages.dsokBilling.invoices.${key}`, { defaultValue: def });
  // Slovenské popisy stavov (default; i18n kľúč ich môže prebiť). Bez toho sa zobrazoval anglický kód.
  const STATUS_LABEL_SK = {
    created: "Vytvorená",
    sent: "Odoslaná",
    paid: "Zaplatená",
    overdue: "Po splatnosti",
    cancelled: "Zrušená",
    error: "Chyba",
  };
  const statusLabel = (s) =>
    t(`pages.dsokBilling.invoices.statuses.${s}`, {
      defaultValue: STATUS_LABEL_SK[s] || s,
    });

  // Dni po splatnosti: len pre neuhradené/nestornované faktúry so splatnosťou v minulosti.
  // Vracia kladné číslo dní, 0 (ešte nie je po splatnosti) alebo null (neaplikovateľné).
  const daysOverdue = (inv) => {
    if (!inv?.dueDate) return null;
    const paid = inv.paymentStatus === "paid" || inv.status === "paid";
    if (paid || inv.status === "cancelled") return null;
    const due = new Date(inv.dueDate);
    if (Number.isNaN(due.getTime())) return null;
    const diff = Math.floor((Date.now() - due.getTime()) / 86400000);
    return diff > 0 ? diff : 0;
  };

  // Server-side fetch: načíta LEN aktuálnu stránku (pageSize) + sumár a total z backendu, takže sa
  // pri veľkom počte faktúr neťahá všetko naraz. Beží pri zmene stránky/veľkosti alebo reloadToken.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getAllBillingInvoices({
          dateFrom,
          dateTo,
          status,
          page: page + 1,
          pageSize,
        });
        if (cancelled) return;
        if (res?.success) {
          const all = Array.isArray(res.data) ? res.data : [];
          setSummary(
            res.summary || { count: 0, withoutVat: 0, vat: 0, withVat: 0 },
          );
          if (res.pagination) {
            // Nový backend už vrátil len danú stránku.
            setInvoices(all);
            setTotal(res.pagination.total ?? res.summary?.count ?? all.length);
          } else {
            // Starší backend bez stránkovania vráti všetko → orežeme stránku na strane klienta,
            // aby stránkovanie fungovalo aj počas rolloutu (kým sa backend nenasadí).
            setTotal(res.summary?.count ?? all.length);
            const start = page * pageSize;
            setInvoices(all.slice(start, start + pageSize));
          }
        } else {
          enqueueSnackbar(label("loadError", "Nepodarilo sa načítať faktúry"), {
            variant: "error",
          });
        }
      } catch (e) {
        if (!cancelled) {
          enqueueSnackbar(
            `${label("loadError", "Nepodarilo sa načítať faktúry")}: ${e?.message || ""}`,
            { variant: "error" },
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, reloadToken]);

  // Obnoví aktuálnu stránku (zachová číslo stránky).
  const reload = () => setReloadToken((x) => x + 1);
  // Aplikuje filtre: skočí na prvú stránku a načíta.
  const applyFilters = () => {
    setPage(0);
    setReloadToken((x) => x + 1);
  };

  // Globálny import stavu úhrady z Abry pre VŠETKÝCH zákazníkov (rovnaký endpoint ako cron).
  // Po dokončení znova načíta zoznam, aby sa prejavili zmenené stavy.
  const handleRefreshPayments = async () => {
    setSyncingPayments(true);
    try {
      const res = await refreshBillingPayments();
      if (res?.success) {
        const s = res.data || {};
        enqueueSnackbar(
          label(
            "paymentsDone",
            `Stavy úhrad aktualizované — skontrolované: ${s.checked ?? 0}, zaplatené: ${
              s.paid ?? 0
            }, chyby: ${s.errors ?? 0}`,
          ),
          { variant: s.errors > 0 ? "warning" : "success" },
        );
        reload();
      } else if (res?.reason === "already_running") {
        enqueueSnackbar(label("paymentsRunning", "Import stavov už beží, skús neskôr."), {
          variant: "info",
        });
      }
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.reason ||
          err?.message ||
          label("paymentsError", "Import stavov úhrad z Abry zlyhal"),
        { variant: "error" },
      );
    } finally {
      setSyncingPayments(false);
    }
  };

  const handleDownloadPdf = async (inv) => {
    setPdfLoadingId(inv._id);
    try {
      const blob = await downloadBillingInvoicePdf(inv._id);
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      enqueueSnackbar(
        label("pdfError", "PDF sa nepodarilo stiahnuť (faktúra nemá PDF v Abre?)"),
        { variant: "error" },
      );
    } finally {
      setPdfLoadingId(null);
    }
  };

  // Faktúra, ktorej má zmysel poslať upomienku (neuhradená + po splatnosti / stav overdue).
  const canRemind = (inv) => {
    if (!inv) return false;
    if (inv.paymentStatus === "paid" || inv.status === "paid" || inv.status === "cancelled") {
      return false;
    }
    if (inv.status === "overdue") return true;
    if (inv.dueDate) {
      const due = new Date(inv.dueDate).getTime();
      if (!Number.isNaN(due) && due < Date.now()) return true;
    }
    return false;
  };

  const reminderReasonLabel = (reason) => {
    const map = {
      reminders_live_disabled:
        "Odosielanie upomienok reálnym zákazníkom je dočasne vypnuté (testovacia fáza).",
      reminder_sending_disabled:
        "Odosielanie upomienok je vypnuté v systémových nastaveniach.",
      not_overdue: "Faktúra nie je po splatnosti.",
      already_sent_today: "Upomienka už bola dnes poslaná.",
      no_billing_email: "Chýba fakturačný email.",
      no_billing_phone: "Chýba fakturačné telefónne číslo.",
      email_disabled: "Odosielanie je vypnuté (master flag).",
      notification_not_configured: "Notifikačný server nie je nakonfigurovaný.",
      notif_failed: "Notifikačný server odmietol odoslanie.",
      pdf_failed: "PDF faktúry z Abry sa nepodarilo získať.",
    };
    return map[reason] || reason || "Neznáma chyba";
  };

  const openReminder = (inv, test = false) => {
    setReminderChannels({ email: true, sms: true });
    setReminderTest(test);
    if (test) {
      let def = "";
      try {
        def = getBEUrl();
      } catch (e) {
        def = "";
      }
      def = String(def || "").replace(/\/+$/, "");
      const match = backendOptions.find((o) => o.url === def);
      setReminderBackend(match ? match.url : backendOptions[0].url);
    }
    setReminderInvoice(inv);
  };

  const doSendReminder = async () => {
    const inv = reminderInvoice;
    if (!inv?._id) return;
    const channels = [];
    if (reminderChannels.email) channels.push("email");
    if (reminderChannels.sms) channels.push("sms");
    if (channels.length === 0) return;

    const opts = reminderTest
      ? {
          test: true,
          overrideEmail: REMINDER_TEST_EMAIL,
          overridePhone: REMINDER_TEST_PHONE,
          baseUrl: reminderBackend,
        }
      : {};
    const backendHost = (reminderBackend || "").replace(/^https?:\/\//, "");

    setReminderSending(true);
    const results = [];
    for (const ch of channels) {
      try {
        const r = await sendBillingInvoiceReminder(inv._id, ch, false, opts);
        results.push({ ch, ok: !!r?.success, reason: r?.reason });
      } catch (e) {
        results.push({
          ch,
          ok: false,
          reason: e?.response?.data?.reason || e?.message,
        });
      }
    }
    setReminderSending(false);
    setReminderInvoice(null);

    const prefix = reminderTest ? "Testovacia upomienka" : "Upomienka";
    const okCh = results.filter((r) => r.ok).map((r) => r.ch);
    const failCh = results.filter((r) => !r.ok);
    if (okCh.length) {
      enqueueSnackbar(
        `${prefix} odoslaná (${okCh.join(", ")})${
          reminderTest
            ? ` na ${REMINDER_TEST_EMAIL} / ${REMINDER_TEST_PHONE} · cez ${backendHost}`
            : ""
        }${
          failCh.length
            ? `; zlyhalo: ${failCh
                .map((f) => `${f.ch}: ${reminderReasonLabel(f.reason)}`)
                .join(", ")}`
            : ""
        }`,
        { variant: failCh.length ? "warning" : "success" },
      );
    } else {
      enqueueSnackbar(
        `${prefix} neodoslaná: ${failCh
          .map((f) => `${f.ch}: ${reminderReasonLabel(f.reason)}`)
          .join(", ")}`,
        { variant: "error" },
      );
    }
    reload();
  };

  const resetFilters = () => {
    setDateFrom("");
    setDateTo("");
    setStatus("");
    setPage(0);
    setReloadToken((x) => x + 1);
  };

  return (
    <Box sx={{ p: 3 }}>
      <BillingBreadcrumbs active="invoices" />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography variant="h2">
          {label("title", "Zoznam faktúr")}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            color="success"
            startIcon={
              syncingPayments ? <CircularProgress size={16} /> : <PaidOutlinedIcon />
            }
            onClick={handleRefreshPayments}
            disabled={syncingPayments || loading}
          >
            {label("importAbra", "Import stavov z Abry")}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={reload}
            disabled={loading}
          >
            {label("refresh", "Obnoviť")}
          </Button>
        </Box>
      </Box>

      {/* Filtre */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={label("dateFrom", "Dátum od")}
              InputLabelProps={{ shrink: true }}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={label("dateTo", "Dátum do")}
              InputLabelProps={{ shrink: true }}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              select
              label={label("status", "Stav úhradenia")}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value="">
                {label("statusAll", "Všetky")}
              </MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {statusLabel(s)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="contained" onClick={applyFilters} disabled={loading}>
                {label("apply", "Filtrovať")}
              </Button>
              <Button variant="text" onClick={resetFilters} disabled={loading}>
                {label("reset", "Zrušiť filter")}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Sumár */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              {label("count", "Počet faktúr")}
            </Typography>
            <Typography variant="h4">{summary.count}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              {label("sumWithoutVat", "Spolu bez DPH")}
            </Typography>
            <Typography variant="h4">{fmtMoney(summary.withoutVat)}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              {label("sumVat", "DPH")}
            </Typography>
            <Typography variant="h4">{fmtMoney(summary.vat)}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              {label("sumWithVat", "Spolu s DPH")}
            </Typography>
            <Typography variant="h4" color="primary">
              {fmtMoney(summary.withVat)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabuľka */}
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>{label("invoiceNumber", "Číslo faktúry")}</TableCell>
                <TableCell>{label("customer", "Zákazník")}</TableCell>
                <TableCell>{label("period", "Obdobie")}</TableCell>
                <TableCell>{label("issueDate", "Vystavená")}</TableCell>
                <TableCell>{label("dueDate", "Splatnosť")}</TableCell>
                <TableCell align="right">
                  {label("daysOverdue", "Dni po splatnosti")}
                </TableCell>
                <TableCell>{label("statusCol", "Stav")}</TableCell>
                <TableCell align="right">
                  {label("withoutVat", "Bez DPH")}
                </TableCell>
                <TableCell align="right">{label("vat", "DPH")}</TableCell>
                <TableCell align="right">
                  {label("withVat", "S DPH")}
                </TableCell>
                <TableCell align="center">PDF</TableCell>
                <TableCell align="center">
                  {label("reminderCol", "Upomienka")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {label("empty", "Žiadne faktúry")}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((inv) => {
                  const totals = inv.totalsPreview || {};
                  const hasPdf =
                    inv?.abra?.id && inv?.abra?.mock !== true;
                  return (
                    <TableRow key={inv._id} hover>
                      <TableCell>{inv.invoiceNumber || "—"}</TableCell>
                      <TableCell>
                        {inv.customerName
                          ? `[${inv.customerId}] ${inv.customerName}`
                          : `[${inv.customerId}]`}
                      </TableCell>
                      <TableCell>{inv.periodLabel || inv.periodKey || "—"}</TableCell>
                      <TableCell>{fmtDate(inv.issueDate)}</TableCell>
                      <TableCell>{fmtDate(inv.dueDate)}</TableCell>
                      <TableCell align="right">
                        {(() => {
                          const d = daysOverdue(inv);
                          if (d == null) return "—";
                          if (d === 0) return "0";
                          return (
                            <Typography
                              component="span"
                              variant="body2"
                              color="error"
                              sx={{ fontWeight: 600 }}
                            >
                              {d}
                            </Typography>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={statusLabel(inv.status)}
                          color={STATUS_COLOR[inv.status] || "default"}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {fmtMoney(totals.net)}
                      </TableCell>
                      <TableCell align="right">
                        {fmtMoney(totals.vat)}
                      </TableCell>
                      <TableCell align="right">
                        {fmtMoney(totals.total)}
                      </TableCell>
                      <TableCell align="center">
                        {hasPdf ? (
                          pdfLoadingId === inv._id ? (
                            <CircularProgress size={18} />
                          ) : (
                            <Tooltip title={label("downloadPdf", "Stiahnuť PDF")}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDownloadPdf(inv)}
                              >
                                <PictureAsPdfIcon />
                              </IconButton>
                            </Tooltip>
                          )
                        ) : (
                          <Tooltip title={label("noPdf", "PDF nie je k dispozícii")}>
                            <span>
                              <IconButton size="small" disabled>
                                <PictureAsPdfIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                        {/* LIVE upomienka — dočasne vypnutá (testovacia fáza). */}
                        <Tooltip
                          title={label(
                            "reminderLiveDisabled",
                            "Odosielanie reálnym zákazníkom je dočasne vypnuté (testovacia fáza)",
                          )}
                        >
                          <span>
                            <IconButton size="small" disabled>
                              <NotificationsActiveOutlinedIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {/* TEST upomienka — pošle na naše kontakty, nie zákazníkovi. */}
                        {canRemind(inv) ? (
                          <Tooltip
                            title={label(
                              "sendReminderTest",
                              "Upomienka – test (pošle na naše kontakty)",
                            )}
                          >
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => openReminder(inv, true)}
                            >
                              <ScienceOutlinedIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip
                            title={label(
                              "reminderNotApplicable",
                              "Upomienka len pre neuhradenú faktúru po splatnosti",
                            )}
                          >
                            <span>
                              <IconButton size="small" disabled>
                                <ScienceOutlinedIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[20, 50, 100]}
          labelRowsPerPage={label("rowsPerPage", "Faktúr na stranu:")}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} ${label("of", "z")} ${count}`
          }
        />
        {!loading && invoices.length > 0 && (
          <>
            <Divider />
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 4,
                p: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography variant="body2">
                {label("sumWithoutVat", "Spolu bez DPH")}:{" "}
                <strong>{fmtMoney(summary.withoutVat)}</strong>
              </Typography>
              <Typography variant="body2">
                {label("sumVat", "DPH")}: <strong>{fmtMoney(summary.vat)}</strong>
              </Typography>
              <Typography variant="body2">
                {label("sumWithVat", "Spolu s DPH")}:{" "}
                <strong>{fmtMoney(summary.withVat)}</strong>
              </Typography>
            </Box>
          </>
        )}
      </Paper>

      {/* Potvrdenie odoslania upomienky (kontrolné tlačidlo). */}
      <Dialog
        open={!!reminderInvoice}
        onClose={() => !reminderSending && setReminderInvoice(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {reminderTest
            ? label("reminderConfirmTitleTest", "Poslať TESTOVACIU upomienku?")
            : label("reminderConfirmTitle", "Poslať upomienku?")}
        </DialogTitle>
        <DialogContent>
          {reminderTest ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {label(
                "reminderConfirmWarnTest",
                "TEST: upomienka sa NEODOŠLE zákazníkovi. Pošle sa len na naše kontakty:",
              )}{" "}
              <strong>{REMINDER_TEST_EMAIL}</strong> /{" "}
              <strong>{REMINDER_TEST_PHONE}</strong>.
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {label(
                "reminderConfirmWarn",
                "Upomienka sa zákazníkovi odošle NAOZAJ cez vybrané kanály.",
              )}
            </Alert>
          )}
          <Typography variant="body2" sx={{ mb: 1 }}>
            {label("reminderConfirmBody", "Faktúra")}{" "}
            <strong>{reminderInvoice?.invoiceNumber || "—"}</strong>
            {reminderInvoice?.customerName
              ? ` — [${reminderInvoice.customerId}] ${reminderInvoice.customerName}`
              : reminderInvoice
                ? ` — [${reminderInvoice.customerId}]`
                : ""}
          </Typography>
          {reminderTest && (
            <TextField
              select
              fullWidth
              size="small"
              sx={{ mb: 1 }}
              label={label("reminderTestBackend", "Cieľový backend (test)")}
              value={reminderBackend}
              onChange={(e) => setReminderBackend(e.target.value)}
              helperText={label(
                "reminderTestBackendHelp",
                "Len pre test — kam sa request odošle.",
              )}
            >
              {backendOptions.map((o) => (
                <MenuItem key={o.url} value={o.url}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          )}
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={reminderChannels.email}
                  onChange={(e) =>
                    setReminderChannels((p) => ({ ...p, email: e.target.checked }))
                  }
                />
              }
              label="Email"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={reminderChannels.sms}
                  onChange={(e) =>
                    setReminderChannels((p) => ({ ...p, sms: e.target.checked }))
                  }
                />
              }
              label="SMS"
            />
          </FormGroup>
          <Typography variant="caption" color="text.secondary">
            {reminderTest
              ? label(
                  "reminderConfirmNoteTest",
                  "Testovacie odoslanie: nemení stav faktúry a nepočíta sa do denného limitu.",
                )
              : label(
                  "reminderConfirmNote",
                  "Pošle sa len raz za deň (ochrana proti duplicite). Vyžaduje zapnuté odosielanie upomienok v systémových nastaveniach.",
                )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setReminderInvoice(null)}
            disabled={reminderSending}
          >
            {label("cancel", "Zrušiť")}
          </Button>
          <Button
            variant="contained"
            color={reminderTest ? "info" : "warning"}
            onClick={doSendReminder}
            disabled={
              reminderSending ||
              (!reminderChannels.email && !reminderChannels.sms) ||
              (reminderTest && !reminderBackend)
            }
            startIcon={
              reminderSending ? <CircularProgress size={16} /> : null
            }
          >
            {reminderTest
              ? label("reminderConfirmSendTest", "Áno, poslať test")
              : label("reminderConfirmSend", "Áno, poslať upomienku")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingInvoicesPage;
